import { useEffect, useMemo, useRef, useState } from 'react';
import PartnerCard from './PartnerCard.js';

const SLOT = {
  '-2': { scale: 0.60, opacity: 0.1, z: 1 },
  '-1': { scale: 0.80, opacity: 0.5, z: 2 },
  '0':  { scale: 1,    opacity: 1,   z: 5 },
  '1':  { scale: 0.80, opacity: 0.5, z: 2 },
  '2':  { scale: 0.60, opacity: 0.1, z: 1 },
};

const Y_STEP = 'var(--stack-step)';
const INTERVAL_MS = 5000;
const DRAG_THRESHOLD = 56;
const WHEEL_COOLDOWN_MS = 550;

export default function PartnerStackCarousel({ partners }) {
  const sorted = useMemo(
    () => [...(partners || [])].sort((a, b) =>
      (a.PartnerName || '').localeCompare(b.PartnerName || '', undefined, { sensitivity: 'base' })
    ),
    [partners]
  );

  const [focus, setFocus] = useState(0);
  const [paused, setPaused] = useState(false);
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const count = sorted.length;

  const hoveringRef = useRef(false);
  const draggingRef = useRef(false);
  const dragRef = useRef({ active: false, startY: 0, moved: false, pointerId: null });
  const wheelLockRef = useRef(false);
  const stageRef = useRef(null);

  const syncPaused = () => {
    setPaused(hoveringRef.current || draggingRef.current);
  };

  const step = (direction) => {
    if (count <= 1) return;
    setFocus((current) => (current + direction + count) % count);
    setDragY(0);
  };

  useEffect(() => {
    if (count <= 1 || paused) return;
    const id = setInterval(() => {
      setFocus((current) => (current + 1) % count);
    }, INTERVAL_MS);
    return () => clearInterval(id);
  }, [count, paused, focus]);

  useEffect(() => {
    const node = stageRef.current;
    if (!node || count <= 1) return;

    const onWheel = (event) => {
      event.preventDefault();
      if (wheelLockRef.current) return;
      if (Math.abs(event.deltaY) < 8) return;
      wheelLockRef.current = true;
      const direction = event.deltaY > 0 ? 1 : -1;
      setFocus((current) => (current + direction + count) % count);
      setDragY(0);
      window.setTimeout(() => {
        wheelLockRef.current = false;
      }, WHEEL_COOLDOWN_MS);
    };

    node.addEventListener('wheel', onWheel, { passive: false });
    return () => node.removeEventListener('wheel', onWheel);
  }, [count]);

  const onPointerDown = (event) => {
    if (count <= 1) return;
    dragRef.current = {
      active: true,
      startY: event.clientY,
      moved: false,
      pointerId: event.pointerId
    };
    draggingRef.current = true;
    setIsDragging(true);
    syncPaused();
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const onPointerMove = (event) => {
    if (!dragRef.current.active) return;
    const delta = event.clientY - dragRef.current.startY;
    if (Math.abs(delta) > 8) dragRef.current.moved = true;
    setDragY(delta);
  };

  const endPointer = (event) => {
    if (!dragRef.current.active) return;
    const delta = event.clientY - dragRef.current.startY;
    dragRef.current.active = false;
    draggingRef.current = false;
    setIsDragging(false);
    syncPaused();

    if (delta <= -DRAG_THRESHOLD) step(1);
    else if (delta >= DRAG_THRESHOLD) step(-1);
    else setDragY(0);
  };

  if (count === 0) return null;

  if (count === 1) {
    return (
      <div className="mx-auto w-full max-w-2xl">
        <PartnerCard partner={sorted[0]} />
      </div>
    );
  }

  const offsets = count >= 5 ? [-2, -1, 0, 1, 2] : count === 4 ? [-1, 0, 1, 2] : [-1, 0, 1].slice(0, count);

  return (
    <div
      ref={stageRef}
      className="relative mx-auto w-full max-w-lg sm:max-w-xl lg:max-w-2xl h-[clamp(20rem,52dvh,30rem)] [--stack-step:clamp(4.75rem,7vh,7.5rem)] select-none cursor-grab active:cursor-grabbing touch-none"
      onMouseEnter={() => {
        hoveringRef.current = true;
        syncPaused();
      }}
      onMouseLeave={() => {
        hoveringRef.current = false;
        syncPaused();
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endPointer}
      onPointerCancel={endPointer}
      style={{ perspective: '1400px' }}
    >
      <div className="absolute inset-0 overflow-hidden [mask-image:linear-gradient(to_bottom,transparent,black_6%,black_94%,transparent)]">
        {offsets.map((offset) => {
          const index = (focus + offset + count) % count;
          const partner = sorted[index];
          const slot = SLOT[String(offset)] || SLOT['0'];
          const isFocus = offset === 0;

          return (
            <div
              key={partner.PartnerID}
              className="absolute left-0 right-0 top-[46%] will-change-transform"
              style={{
                transform: `translateY(calc(-50% + (${offset} * ${Y_STEP}) + ${dragY * 0.45}px)) scale(${slot.scale})`,
                zIndex: slot.z,
                opacity: slot.opacity,
                transition: isDragging
                  ? 'none'
                  : 'transform 750ms cubic-bezier(0.22, 1, 0.36, 1), opacity 750ms ease',
              }}
              onClickCapture={(event) => {
                if (dragRef.current.moved) {
                  event.preventDefault();
                  event.stopPropagation();
                  dragRef.current.moved = false;
                  return;
                }
                if (!isFocus) {
                  event.preventDefault();
                  event.stopPropagation();
                  setFocus(index);
                }
              }}
            >
              <PartnerCard
                partner={partner}
                hoverScale={false}
                openOnClick={isFocus}
                className={isFocus ? 'shadow-2xl ring-1 ring-black/10' : 'shadow-md'}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
